import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";

export interface BlockPair {
	nodeA: Node;
	posA: number;
	nodeB: Node;
	posB: number;
}

const matchUnchangedBlocks = (changes: DiffChange[], before: Node, after: Node): BlockPair[] => {
	const touchedA = new Set(changes.map((change) => change.posA).filter((pos) => pos !== undefined));
	const touchedB = new Set(changes.map((change) => change.posB).filter((pos) => pos !== undefined));

	const pairs: BlockPair[] = [];

	const walk = (nodeA: Node, posA: number, nodeB: Node, posB: number) => {
		const childrenA: { node: Node; pos: number }[] = [];
		const childrenB: { node: Node; pos: number }[] = [];

		nodeA.forEach((child, offset) => {
			const pos = posA + offset + 1;
			if (!child.isText && !touchedA.has(pos)) childrenA.push({ node: child, pos });
		});
		nodeB.forEach((child, offset) => {
			const pos = posB + offset + 1;
			if (!child.isText && !touchedB.has(pos)) childrenB.push({ node: child, pos });
		});

		if (childrenA.length !== childrenB.length) return;

		childrenA.forEach((childA, index) => {
			const childB = childrenB[index];
			if (childA.node.type.name !== childB.node.type.name) return;

			pairs.push({ nodeA: childA.node, posA: childA.pos, nodeB: childB.node, posB: childB.pos });
			walk(childA.node, childA.pos, childB.node, childB.pos);
		});
	};

	// Doc children start at offset 0, not pos + 1, so the root is walked separately.
	const rootA: { node: Node; pos: number }[] = [];
	const rootB: { node: Node; pos: number }[] = [];
	before.forEach((child, offset) => {
		if (!touchedA.has(offset)) rootA.push({ node: child, pos: offset });
	});
	after.forEach((child, offset) => {
		if (!touchedB.has(offset)) rootB.push({ node: child, pos: offset });
	});

	if (rootA.length !== rootB.length) return pairs;

	rootA.forEach((childA, index) => {
		const childB = rootB[index];
		if (childA.node.type.name !== childB.node.type.name) return;

		pairs.push({ nodeA: childA.node, posA: childA.pos, nodeB: childB.node, posB: childB.pos });
		walk(childA.node, childA.pos, childB.node, childB.pos);
	});

	return pairs;
};

export default matchUnchangedBlocks;
